import React from 'react';

import PropTypes from 'prop-types';
import CSSModules from 'react-css-modules';
import {injectIntl} from 'react-intl';

import {Lesson, ShrinkText} from '@edume/magnificent';

import {QUESTION_TYPES} from '../../../../../enums/lessons';
import EduMePropTypes from '../../../../../proptypes';
import {getQuestionTypeFromQuizType} from '../../../../../utils/lesson';
import OrderingTaskPreview from '../OrderingTaskPreview';
import QuestionPreviewButton from '../QuestionPreviewButton';

import sharedStyles from '../sharedStyles.module.scss';
import styles from './styles.module.scss';

const {InteractiveActivity, Question} = Lesson;

const QuestionPreview = ({
  prompt,
  shuffleAnswers,
  question,
  questionNumber,
  numberOfQuestions,
  imageUrl,
  thumbnailImageUrl,
  quizType,
  intl,
  isAssessment,
  button,
  isPreview,
  background,
  textColour,
  quizQuestionsContainImages,
  phoneHeight,
}) => {
  const questionNumberText = intl.formatMessage(
    {id: 'Authoring.preview.questionNumber'},
    {questionNumber, numberOfQuestions}
  );
  const defaultPrompt = intl.formatMessage({
    id: `Authoring.editQuestion.${
      question.type === QUESTION_TYPES.ORDERING
        ? 'defaultOrderingQuizPrompt'
        : 'defaultPrompt'
    }`,
  });
  const defaultQuestion = intl.formatMessage({
    id: 'Authoring.editQuestion.questionPlaceholder',
  });
  const defaultAnswer = intl.formatMessage({
    id: 'LessonCanvas.EditQuiz.answerPlaceholder',
  });
  const questionType = question.type ?? getQuestionTypeFromQuizType(quizType);

  return (
    <div
      styleName='activity-preview'
      style={{...(background && {backgroundColor: background})}}
    >
      <InteractiveActivity
        isPreview={isPreview}
        editMode
        question={question?.question || defaultQuestion}
        instruction={prompt || defaultPrompt}
        imageUrl={imageUrl}
        thumbnailImageUrl={thumbnailImageUrl}
        quizQuestionsContainImages={quizQuestionsContainImages}
        shouldAnimate={false}
        textColour={textColour}
        quizType={quizType}
        questionType={questionType}
      >
        <div styleName={`question-preview ${isPreview ? 'preview-mode' : ''}`}>
          {question.type === QUESTION_TYPES.ORDERING ? (
            <OrderingTaskPreview
              button={button}
              phoneHeight={phoneHeight}
              answers={question.options.map(({text}) => text)}
              isPreview={isPreview}
              questionNumberText={questionNumberText}
              numberOfQuestions={numberOfQuestions}
            />
          ) : (
            <Question
              isPreview={isPreview}
              isAssessment={isAssessment}
              editMode
              answers={question?.options.map((option) => ({
                ...option,
                text: option.text || defaultAnswer,
              }))}
              questionNumberText={questionNumberText}
              numberOfQuestions={numberOfQuestions}
              multipleChoice={questionType === QUESTION_TYPES.MULTIPLE_RESPONSE}
              shuffleAnswers={!isPreview && shuffleAnswers}
            />
          )}
        </div>
        {isAssessment && (
          <ShrinkText maxPxWidth={276} startingFontSize='m' minimumFontSize='s'>
            <QuestionPreviewButton text={button.text} />
          </ShrinkText>
        )}
      </InteractiveActivity>
    </div>
  );
};

QuestionPreview.propTypes = {
  prompt: PropTypes.string,
  shuffleAnswers: PropTypes.bool,
  question: PropTypes.shape({
    question: PropTypes.string,
    type: PropTypes.oneOf(Object.values(QUESTION_TYPES)),
    options: PropTypes.arrayOf(
      PropTypes.shape({
        text: PropTypes.string,
        isCorrect: PropTypes.bool,
        tip: PropTypes.string,
      })
    ).isRequired,
  }).isRequired,
  questionNumber: PropTypes.number.isRequired,
  numberOfQuestions: PropTypes.number.isRequired,
  imageUrl: PropTypes.string,
  thumbnailImageUrl: PropTypes.string,
  quizType: PropTypes.string.isRequired,
  intl: PropTypes.object.isRequired,
  isAssessment: PropTypes.bool,
  button: EduMePropTypes.buttonParams,
  background: PropTypes.string,
  textColour: PropTypes.string,
  isPreview: PropTypes.bool,
  phoneHeight: PropTypes.number.isRequired,
  quizQuestionsContainImages: PropTypes.bool,
};

QuestionPreview.defaultProps = {
  isPreview: false,
};

export default injectIntl(
  CSSModules(
    QuestionPreview,
    {...styles, ...sharedStyles},
    {allowMultiple: true}
  )
);
